import { Link } from "react-router-dom";
import { Instagram, Youtube } from "lucide-react";
import { MASCOT_URL, MORE_LINKS, FOURTHWALL_URL, PawIcon, SOCIAL_LINKS, TikTokIcon, XIcon } from "@/lib/brand";

const SOCIAL_ICONS = { instagram: Instagram, youtube: Youtube, tiktok: TikTokIcon, x: XIcon };

const EXPLORE_LINKS = [
    { to: "/about", label: "About" },
    { to: "/pan-school", label: "PAN School" },
    { to: "/my-5", label: "MY 5" },
    { to: "/media", label: "Media" },
    { to: FOURTHWALL_URL, label: "Shop", external: true },
];

const LEGAL_LINKS = [
    { to: "/privacy", label: "Privacy Policy" },
    { to: "/terms", label: "Terms of Service" },
    { to: "/refund-policy", label: "Refund & Return Policy" },
];

export default function Footer() {
    return (
        <footer className="border-t border-white/10 bg-panel/60" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
                <div>
                    <div className="flex items-center gap-4">
                        <img
                            src={MASCOT_URL}
                            alt="PURP ALERT #5 — the official mascot"
                            className="w-16 h-16 rounded-2xl object-cover border border-purp/40 card-glow"
                        />
                        <div className="leading-none">
                            <p className="font-display italic font-extrabold text-purp text-2xl">PURP ALERT</p>
                            <p className="font-mono text-[10px] tracking-[0.45em] text-slate-300 mt-1">NETWORK</p>
                        </div>
                    </div>
                    <p className="mt-6 text-slate-400 text-sm max-w-md leading-relaxed">
                        A creative development and acquisition network for independent artists and aspiring
                        creatives. More than a talent agency. Bigger than a record company.
                    </p>
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-purp-neon flex items-center gap-2">
                        <PawIcon className="w-4 h-4" /> Find Your Lane. Build Your Brand. Create Your Opportunity.
                    </p>
                    {SOCIAL_LINKS.length > 0 && (
                        <div className="mt-7" id="connect" data-testid="footer-socials">
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4">Connect with PAN</p>
                            <div className="grid grid-cols-2 gap-2.5 max-w-xs">
                                {SOCIAL_LINKS.map((s) => {
                                    const Icon = SOCIAL_ICONS[s.id];
                                    return (
                                        <a
                                            key={s.id}
                                            href={s.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`footer-social-${s.id}`}
                                            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] text-slate-300 hover:text-purp-neon hover:border-purp/50 transition-colors"
                                        >
                                            <Icon className="w-4 h-4 shrink-0" /> {s.label}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-5">Explore</p>
                    <ul className="space-y-3">
                        {EXPLORE_LINKS.map((l) => (
                            <li key={l.to}>
                                {l.external ? (
                                    <a href={l.to} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-300 hover:text-purp-neon transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                        {l.label}
                                    </a>
                                ) : (
                                    <Link to={l.to} className="text-sm text-slate-300 hover:text-purp-neon transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                        {l.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-5">Network</p>
                    <ul className="space-y-3">
                        {MORE_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link to={l.to} className="text-sm text-slate-300 hover:text-purp-neon transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-5">Trust</p>
                    <ul className="space-y-3 mb-6">
                        {LEGAL_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link to={l.to} className="text-sm text-slate-300 hover:text-purp-neon transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <a href="mailto:PurpAlertNetwork@proton.me" className="text-sm text-slate-300 hover:text-purp-neon transition-colors" data-testid="footer-contact-link">
                                PurpAlertNetwork@proton.me
                            </a>
                        </li>
                    </ul>
                    <div className="space-y-3 text-xs text-slate-500 leading-relaxed">
                        <p>
                            <span className="text-slate-300 font-semibold">Now:</span> PURP ALERT NETWORK (PAN LLC) —
                            an active business being built today.
                        </p>
                        <p>
                            <span className="text-slate-300 font-semibold">Future:</span> PURP ALERT INC. — a
                            separate nonprofit vision. Not yet formed, not a 501(c)(3).
                        </p>
                    </div>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        © 2026 PURP ALERT NETWORK. All lanes reserved.
                    </p>
                    <div className="flex items-center gap-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                            Create — Build — Grow — Connect
                        </p>
                        <Link to="/admin" className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600 hover:text-purp-neon transition-colors" data-testid="footer-admin-link">
                            Admin
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
